import React, { useState } from 'react';
import styled from 'styled-components';

// Segmented pill tabs replacing react-bootstrap's default tabs (2026-07-30):
// the old ones wrapped into multiple rows on phones and read as bare links.
// These scroll horizontally in one row and match the app's newer card
// language. API-compatible with the old component ({ tabs, defaultTab,
// activeKey, onSelect }).

const TabBar = styled.div`
  display: flex;
  gap: 0.35rem;
  padding: 0.3rem;
  background: #eef1f6;
  border-radius: 12px;
  margin-bottom: 1.1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled.button`
  flex: 0 0 auto;
  border: none;
  border-radius: 9px;
  padding: 0.45rem 0.95rem;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#4f46e5' : '#5b6474')};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(15, 28, 51, 0.12)' : 'none'};

  &:hover {
    color: ${({ $active }) => ($active ? '#4f46e5' : '#0f1c33')};
  }
`;

const getDefaultTab = (tabs, defaultTab) => {
  if (defaultTab) {
    return defaultTab;
  }

  return tabs[0].key || 'tab-0';
};

const Tabs = ({ tabs, defaultTab, activeKey, onSelect }) => {
  const controlled = activeKey !== undefined;
  const [internalKey, setInternalKey] = useState(() =>
    getDefaultTab(tabs, defaultTab),
  );
  const currentKey = controlled
    ? activeKey || getDefaultTab(tabs, defaultTab)
    : internalKey;

  const handleSelect = (key) => {
    if (!controlled) setInternalKey(key);
    onSelect?.(key);
  };

  // When currentKey matches no tab (deep link to a tab this user cannot
  // see), fall back to the first tab AND highlight it, so the bar never
  // shows zero active pills.
  const resolvedKey = tabs.some(
    (t, tIdx) => (t.key || `tab-${tIdx}`) === currentKey,
  )
    ? currentKey
    : tabs[0]?.key || 'tab-0';

  return (
    <>
      <TabBar role="tablist">
        {tabs.map((t, tIdx) => {
          const key = t.key || `tab-${tIdx}`;
          return (
            <TabButton
              key={key}
              type="button"
              role="tab"
              aria-selected={key === resolvedKey}
              $active={key === resolvedKey}
              onClick={() => handleSelect(key)}
            >
              {t.label}
            </TabButton>
          );
        })}
      </TabBar>
      {/* Every pane stays MOUNTED (hidden, not unmounted) — matching the old
          react-bootstrap tabs so in-progress state survives tab switches:
          a chat stream, an upload progress bar, a half-typed form. */}
      {tabs.map((t, tIdx) => {
        const key = t.key || `tab-${tIdx}`;
        return (
          <div key={key} role="tabpanel" hidden={key !== resolvedKey}>
            {t.content}
          </div>
        );
      })}
    </>
  );
};

export default Tabs;
